/**
 * 작성자 : 한영광
 * 날짜 : 2025.08.12
 * 기능 : 마스터관리-LLM 쓰러짐 감지 데이터관리 모달
 */

import { useState, useEffect, Dispatch, SetStateAction, useRef } from 'react';
import { toast } from 'react-toastify';
import { IModal } from 'customTypes';
import { ModalBackground, Modal } from '../../../assets/styles/Modal';
import { BtnBlue } from '../../Button';

import useOnKeydownF9 from '../../../utils/useOnKeydownF9';
import { useTranslation } from 'react-i18next';
import useOnKeydownPgUp from '../../../utils/useOnKeydownPgUp';
import useOnKeydownPgDn from '../../../utils/useOnKeydownPgDn';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiPatch } from '../../../services/_common';
import { isImageValid } from '../../../utils/isImageValid';
import TumbleFeedbackGuideModal from './TumbleFeedbackGuideModal';
import { TUMBLE_FALSE_POSITIVE_CAUSES, TUMBLE_FALSE_NEGATIVE_CAUSES } from './tumbleFeedbackExamples';

interface IProps {
  refetch: () => void;
  reportArray: any[];
  setReportArray: Dispatch<SetStateAction<any[]>>;
  setOpenModal: Dispatch<SetStateAction<IModal>>;
  index: number;
  setRowKey: Dispatch<SetStateAction<any>>;
  listCount: number;
}

const TumbleDetectionModal = ({ refetch, setOpenModal, index, reportArray, setReportArray, setRowKey, listCount }: IProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [openGuideModal, setOpenGuideModal] = useState<IModal>({ status: false, type: 'feedbackGuide', isOverlappingModal: true });
  const { isF9Pressed, setIsF9Pressed } = useOnKeydownF9();
  const { isPgUpPressed, setIsPgUpPressed } = useOnKeydownPgUp();
  const { isPgDnPressed, setIsPgDnPressed } = useOnKeydownPgDn();
  const [currentIndex, setCurrentIndex] = useState<number>(index);
  const [rowState, setRowState] = useState(reportArray[index]);
  const [actualTumbleStatus, setActualTumbleStatus] = useState('선택');
  const [misclassificationCause, setMisclassificationCause] = useState('');
  const [customMisclassificationCause, setCustomMisclassificationCause] = useState('');
  const [loading, setLoading] = useState(false);

  const [selectedFallType, setSelectedFallType] = useState('');
  const [selectedDetectionFailure, setSelectedDetectionFailure] = useState('');

  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!rowState) return;
    if (rowState?.imageUrl) {
      isImageValid(rowState.imageUrl);
    }
    const findCause = Object.entries(TUMBLE_FALSE_POSITIVE_CAUSES).find(([, cause]) => cause.description === rowState.misclassificationCause);
    setActualTumbleStatus(rowState.actualTumbleStatus || '선택');
    setMisclassificationCause(findCause ? findCause[0] : 'OTHER');
    setCustomMisclassificationCause(findCause ? '' : rowState.misclassificationCause === 'n/a' ? '' : rowState.misclassificationCause);
  }, [rowState]);

  useEffect(() => {
    setRowState(reportArray[index]);
    setLoading(false);
  }, [reportArray]);

  useEffect(() => {
    if (isF9Pressed) {
      setIsF9Pressed(false);
    }
  }, [isF9Pressed]);

  useEffect(() => {
    if (isPgUpPressed) {
      onClickPrev();
      setIsPgUpPressed(false);
    }
  }, [isPgUpPressed]);

  useEffect(() => {
    if (isPgDnPressed) {
      onClickNext();
      setIsPgDnPressed(false);
    }
  }, [isPgDnPressed]);

  useEffect(() => {
    if (reportArray?.length > 0 && currentIndex > -1) {
      setRowState(reportArray[currentIndex]);
    }
  }, [currentIndex]);

  const onClickClose = () => {
    setOpenModal((prev: IModal) => ({ ...prev, status: false }));
  };

  const onClickPrev = () => {
    if (reportArray) {
      if (currentIndex !== undefined && currentIndex > 0) {
        const newIndex = currentIndex - 1;
        if (listCount) {
          navigate({
            pathname: location.pathname,
            search: `?pageNum=${Math.floor(newIndex / listCount) + 1}`,
          });
        }
        if (setRowKey) setRowKey(newIndex);
        setCurrentIndex(newIndex);
      } else toast.info(t('이전 내역이 없습니다'));
    }
  };

  const onClickNext = () => {
    if (reportArray) {
      if (currentIndex !== undefined && reportArray.length - 1 > currentIndex) {
        const newIndex = currentIndex + 1;
        if (listCount) {
          navigate({
            pathname: location.pathname,
            search: `?pageNum=${Math.floor(newIndex / listCount) + 1}`,
          });
        }
        if (setReportArray) setReportArray(reportArray);
        if (setRowKey) setRowKey(newIndex);
        setCurrentIndex(newIndex);
      } else toast.info(t('다음 내역이 없습니다'));
    }
  };

  const onClickSave = async () => {
    if (actualTumbleStatus === '선택') {
      toast.warn(t('실제 쓰러짐 여부를 선택해주세요'));
      return;
    }
    let patchMisclassificationCause = '';
    if (rowState.detectionResult === 'TUMBLE_DETECTED' && actualTumbleStatus === '정상') {
      if (!misclassificationCause) {
        toast.warn(t('오탐지 원인을 선택해주세요'));
        return;
      }
      const cause = TUMBLE_FALSE_POSITIVE_CAUSES[misclassificationCause as keyof typeof TUMBLE_FALSE_POSITIVE_CAUSES];
      patchMisclassificationCause = misclassificationCause === 'OTHER' ? customMisclassificationCause : cause?.description ? cause.description : '';
    } else if (rowState.detectionResult === 'NO_TUMBLE_DETECTED' && actualTumbleStatus === '쓰러짐') {
      if (!selectedDetectionFailure) {
        toast.warn(t('감지 실패 주요 원인을 선택해주세요'));
        return;
      }
      const fallTypePart = selectedFallType ? `쓰러짐유형:${selectedFallType}` : '';
      const causePart = `감지실패원인:${selectedDetectionFailure}`;
      const customPart = customMisclassificationCause ? `추가설명:${customMisclassificationCause}` : '';
      patchMisclassificationCause = [fallTypePart, causePart, customPart].filter(Boolean).join(' / ');
    }
    setLoading(true);
    const result = await apiPatch({
      path: `/llm/tumbleDetectionResults/${rowState.id}`,
      req: {
        misclassificationCause: patchMisclassificationCause,
        actualTumbleStatus,
      },
      contentType: 'application/json',
    });
    if (result.data.statusCode === 200) {
      toast.success(t(result.data.message));
      refetch();
    } else {
      toast.error(t(result.data.message));
    }
  };

  const helpIconStyle: React.CSSProperties = {
    fontSize: '20px',
    color: '#007bff',
    cursor: 'pointer',
    padding: '2px',
    borderRadius: '50%',
    transition: 'all 0.2s ease',
  };

  const causeButtonStyle = (selected: boolean): React.CSSProperties => ({
    border: selected ? '2px solid #007bff' : '1px solid #dee2e6',
    borderRadius: '6px',
    backgroundColor: selected ? '#e7f3ff' : '#fff',
    color: selected ? '#007bff' : '#495057',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: selected ? '600' : '400',
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    height: '40px',
    padding: '4px 10px',
  });

  return (
    <ModalBackground role='presentation'>
      <Modal>
        <div
          className='modal height-fit signup'
          role='presentation'
          onClick={(event) => {
            event.stopPropagation();
          }}
          ref={modalRef}
        >
          <div className='inputForm-head'>
            <div className='modalHeaderTitle' style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {t('쓰러짐 분석 결과')}
            </div>
            <div className='closeBtn' onClick={onClickClose} role='presentation'>
              <span className='material-symbols-rounded'>close</span>
            </div>
          </div>
          <div className='black flexColumn flex-between col-w100'>
            <div className='filter-option-grid grid1'>
              <div className='viewOnly'>
                <label htmlFor='hsName' className='w7rem'>
                  {t('현장 명')}
                </label>
                <div>{`${rowState?.hName} ${rowState?.sName}`}</div>
              </div>
              <div className='viewOnly'>
                <label htmlFor='detectionResult' className='w7rem'>
                  {t('분석 결과')}
                </label>
                <div>{rowState?.detectionResult === 'TUMBLE_DETECTED' ? t('쓰러짐') : t('정상')}</div>
              </div>
              <div className='viewOnly'>
                <label htmlFor='llmReasoning' className='w7rem'>
                  {t('LLM 판단 근거')}
                </label>
                <div
                  style={{
                    minHeight: '70px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    lineHeight: '1.5',
                    fontSize: '14px',
                    padding: '8px',
                    borderRadius: '4px',
                    backgroundColor: 'var(--tonal)',
                    boxSizing: 'border-box',
                    display: 'block',
                  }}
                >
                  {rowState?.llmReasoning}
                </div>
              </div>
              <div className='viewOnly'>
                <label htmlFor='detectionTime' className='w7rem'>
                  {t('감지 일시')}
                </label>
                <div>{rowState?.detectionTime}</div>
              </div>
              {rowState?.userFeedback && (
                <div className='viewOnly'>
                  <label htmlFor='userFeedback' className='w7rem'>
                    {t('사용자 피드백')}
                  </label>
                  <div
                    style={{
                      minHeight: '70px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                      lineHeight: '1.5',
                      fontSize: '14px',
                      padding: '8px',
                      border: '1px solid #e9ecef',
                      borderRadius: '4px',
                      backgroundColor: '#f8f9fa',
                      boxSizing: 'border-box',
                      display: 'block',
                    }}
                  >
                    {rowState?.userFeedback}
                  </div>
                </div>
              )}
              <div className='selectWrapper'>
                <label htmlFor='actualTumbleStatus' className='w7rem'>
                  {t('실제 쓰러짐 여부')} <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <select className='w7rem' value={actualTumbleStatus} onChange={(e) => setActualTumbleStatus(e.target.value)} id='actualTumbleStatus'>
                  <option value='선택' hidden>
                    {t('선택')}
                  </option>
                  <option value='쓰러짐'>{t('쓰러짐')}</option>
                  <option value='정상'>{t('정상')}</option>
                </select>
              </div>

              {rowState?.detectionResult === 'TUMBLE_DETECTED' && actualTumbleStatus === '정상' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <label className='w7rem' style={{ fontWeight: '500', color: '#333' }}>
                      {t('오탐지 원인')} <span style={{ color: '#dc3545' }}>*</span>
                    </label>
                    <span
                      className='material-symbols-rounded'
                      onClick={() => setOpenGuideModal((prev) => ({ ...prev, status: true, type: 'FALSE_POSITIVE' }))}
                      style={helpIconStyle}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#e7f3ff';
                        e.currentTarget.style.transform = 'scale(1.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                      title={t('피드백 작성 가이드')}
                      role='presentation'
                    >
                      help
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '8px', width: '100%' }}>
                    {Object.entries(TUMBLE_FALSE_POSITIVE_CAUSES).map(([key, cause]) => (
                      <button
                        key={key}
                        type='button'
                        onClick={() => setMisclassificationCause(key)}
                        style={causeButtonStyle(misclassificationCause === key)}
                        onMouseEnter={(e) => {
                          if (misclassificationCause !== key) {
                            e.currentTarget.style.borderColor = '#007bff';
                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (misclassificationCause !== key) {
                            e.currentTarget.style.borderColor = '#dee2e6';
                            e.currentTarget.style.backgroundColor = '#fff';
                          }
                        }}
                      >
                        {cause.code}
                      </button>
                    ))}
                  </div>
                  {misclassificationCause === 'OTHER' && (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <label style={{ minWidth: '120px', fontWeight: '500', color: '#333' }}>{t('기타 원인 입력')}</label>
                      <input
                        type='text'
                        name='customMisclassificationCause'
                        value={customMisclassificationCause}
                        onChange={(e) => setCustomMisclassificationCause(e.target.value)}
                        placeholder={t('구체적인 오탐지 원인을 입력해주세요')}
                        maxLength={100}
                        style={{ flex: 1, border: '1px solid #dee2e6', borderRadius: '4px', fontSize: '14px', outline: 'none', transition: 'border-color 0.2s ease' }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#007bff';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#dee2e6';
                        }}
                      />
                    </div>
                  )}
                </div>
              )}

              {rowState?.detectionResult === 'NO_TUMBLE_DETECTED' && actualTumbleStatus === '쓰러짐' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <label className='w7rem' style={{ fontWeight: '500', color: '#333' }}>
                      {t('미탐지 원인')} <span style={{ color: '#dc3545' }}>*</span>
                    </label>
                    <span
                      className='material-symbols-rounded'
                      onClick={() => setOpenGuideModal((prev) => ({ ...prev, status: true, type: 'FALSE_NEGATIVE' }))}
                      style={helpIconStyle}
                      title={t('피드백 작성 가이드')}
                      role='presentation'
                    >
                      help
                    </span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <label style={{ fontWeight: '500', color: '#6c757d', fontSize: '13px', minWidth: '100px' }}>{TUMBLE_FALSE_NEGATIVE_CAUSES.FALL_TYPE.label}</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', flex: 1 }}>
                      {Object.entries(TUMBLE_FALSE_NEGATIVE_CAUSES.FALL_TYPE)
                        .filter(([key]) => !['required', 'label'].includes(key))
                        .map(([key, value]) => {
                          const item = value as { code: string; description: string; examples: string[] };
                          return (
                            <button
                              key={key}
                              type='button'
                              onClick={() => setSelectedFallType(selectedFallType === item.code ? '' : item.code)}
                              style={{
                                height: '40px',
                                padding: '4px 10px',
                                border: `1.5px solid ${selectedFallType === item.code ? '#6c757d' : '#dee2e6'}`,
                                borderRadius: '4px',
                                backgroundColor: selectedFallType === item.code ? '#f1f3f5' : 'white',
                                color: selectedFallType === item.code ? '#495057' : '#adb5bd',
                                fontSize: '12px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                fontWeight: selectedFallType === item.code ? '500' : '400',
                                minWidth: 'fit-content',
                                whiteSpace: 'nowrap',
                              }}
                              title={item.description}
                            >
                              {item.code}
                            </button>
                          );
                        })}
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <label style={{ fontWeight: '600', color: '#333', fontSize: '13px', minWidth: '100px' }}>
                      {TUMBLE_FALSE_NEGATIVE_CAUSES.DETECTION_FAILURE_CAUSE.label} <span style={{ color: '#dc3545' }}>*</span>
                    </label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', flex: 1 }}>
                      {Object.entries(TUMBLE_FALSE_NEGATIVE_CAUSES.DETECTION_FAILURE_CAUSE)
                        .filter(([key]) => !['required', 'label'].includes(key))
                        .map(([key, value]) => {
                          const item = value as { code: string; description: string; examples: string[] };
                          return (
                            <button
                              key={key}
                              type='button'
                              onClick={() => setSelectedDetectionFailure(item.code)}
                              style={{
                                height: '40px',
                                padding: '4px 10px',
                                border: `1.5px solid ${selectedDetectionFailure === item.code ? '#007bff' : '#dee2e6'}`,
                                borderRadius: '4px',
                                backgroundColor: selectedDetectionFailure === item.code ? '#e7f3ff' : 'white',
                                color: selectedDetectionFailure === item.code ? '#007bff' : '#333',
                                fontSize: '12px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                fontWeight: selectedDetectionFailure === item.code ? '500' : '400',
                                minWidth: 'fit-content',
                                whiteSpace: 'nowrap',
                              }}
                              title={item.description}
                            >
                              {item.code}
                            </button>
                          );
                        })}
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <label style={{ minWidth: '120px', fontWeight: '500', color: '#333' }}>{t('기타 원인 입력')}</label>
                    <input
                      type='text'
                      name='customMisclassificationCause'
                      value={customMisclassificationCause}
                      onChange={(e) => setCustomMisclassificationCause(e.target.value)}
                      placeholder={t('구체적인 미탐지 원인을 입력해주세요')}
                      maxLength={100}
                      style={{ flex: 1, border: '1px solid #dee2e6', borderRadius: '4px', fontSize: '14px', outline: 'none', transition: 'border-color 0.2s ease' }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#007bff';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#dee2e6';
                      }}
                    />
                  </div>
                </div>
              )}

              <div className='flex-center'>
                <img src={rowState?.imageUrl} alt='image' height='250rem' />
              </div>
            </div>
          </div>
          <div className='modal-footer flex-between'>
            <div className='flex-basic arrows'>
              <BtnBlue onClick={onClickPrev}>
                <span className='material-symbols-rounded'>arrow_back_ios_new</span>
                <p>{t('이전')}</p>
                <div>
                  <p>Page</p>
                  <p>Up</p>
                </div>
              </BtnBlue>
              <BtnBlue onClick={onClickNext}>
                <div>
                  <p>Page</p>
                  <p>Down</p>
                </div>
                <p>{t('다음')}</p>
                <span className='material-symbols-rounded'>arrow_forward_ios</span>
              </BtnBlue>
            </div>
            <div className='flex-basic'>{loading ? <BtnBlue disabled>{t('저장중')}</BtnBlue> : <BtnBlue onClick={onClickSave}>{t('저장')}</BtnBlue>}</div>
          </div>
        </div>
      </Modal>
      {openGuideModal.status && <TumbleFeedbackGuideModal openModal={openGuideModal} setOpenModal={setOpenGuideModal} />}
    </ModalBackground>
  );
};

export default TumbleDetectionModal;
